import { pickBy, isNil, isEmpty, toUpper, omit, set } from "lodash"
import { SETUP_GOALS } from "connected-components/optimize-experiments/constants"
import type { SecuritySearchFilter } from "resources/filters"
import { percentageNoSymbolFormatter } from "helpers/formatters/numbers"
import { IsoDate } from "helpers/types/dates"
import {
  OptimizationConfiguration,
  OptimizationConstraints,
  OptimizationObjective,
  OptimizationOptions,
  ApiFactorRule,
} from "connected-components/optimize-experiments/hooks/types"
import { FactorRuleDisplay } from "connected-components/optimize-experiments/components/factor-rules/types"
import { SetupSection, RulesSection, UniverseSection } from "connected-components/optimize-experiments/configuration-sections"
import { SelectedInlineInstrumentDate, PositionSetEquity, PositionSetOtherAsset } from "helpers/types/instruments/selected"
import { EXPOSURE_CONSTRAINT_WEIGHTINGS } from "helpers/contentSetConstants"

export interface OptimizationSecuritiesInput {
  long: SecuritiesInput
  short: SecuritiesInput
  longOrShort: SecuritiesInput
}

export interface OptimizationSecuritySearchInput {
  filter: SecuritySearchFilter[]
  take?: number
}

export interface SecuritiesInput {
  securitySearch: Partial<OptimizationSecuritySearchInput>
}

interface GraphQLOptimizationConfiguration {
  objectives: Partial<OptimizationObjective>[]
  constraints: Partial<OptimizationConstraints>
  options: Partial<OptimizationOptions>
  forecast: string | null
  universe: UniverseSection
  securities: Partial<OptimizationSecuritiesInput>
}

export interface MarketValues {
  longMarketValue: number
  shortMarketValue: number
}

export const LONG_SHORT_PORTFOLIO_TYPE = "LONG_SHORT"
const LONG_PORTFOLIO_TYPE = "LONG"
const SHORT_PORTFOLIO_TYPE = "SHORT"

export type PortfolioType = typeof LONG_SHORT_PORTFOLIO_TYPE | typeof LONG_PORTFOLIO_TYPE | typeof SHORT_PORTFOLIO_TYPE

interface FormSections {
  setup: SetupSection
  rules: RulesSection
  universe: UniverseSection
}

const DEFAULT_MARKET_VALUES = { longMarketValue: 0, shortMarketValue: 0 }

function getObjectiveFromSetup(setup: SetupSection): OptimizationObjective {
  switch (setup.goal) {
    case SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK: {
      return { minimizeFactorRisk: true }
    }
    case SETUP_GOALS.TARGET_TOTAL_RISK: {
      if (!setup.targetTotalRiskValue) {
        throw new Error("Target total risk value is required")
      }
      if (!setup.forecast || setup.forecast === "none") {
        throw new Error("Forecast is required")
      }
      return { targetTotalRisk: parseFloat(setup.targetTotalRiskValue) / 100 }
    }
    case SETUP_GOALS.MIN_VOLATILITY: {
      return { minimizeTotalRisk: true }
    }
    default: {
      const _exhaustive: never = setup.goal
      throw new Error(`Unknown goal: ${_exhaustive}`)
    }
  }
}

function getSetupFromObjective(objective: Partial<OptimizationObjective>): SetupSection {
  if (objective.targetTotalRisk !== undefined) {
    return {
      goal: SETUP_GOALS.TARGET_TOTAL_RISK,
      targetTotalRiskValue: (objective.targetTotalRisk * 100).toFixed(2),
    }
  }

  if (objective.minimizeTotalRisk !== undefined) {
    return {
      goal: SETUP_GOALS.MIN_VOLATILITY,
    }
  }

  if (objective.minimizeFactorRisk !== undefined) {
    return {
      goal: SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK,
    }
  }

  throw new Error(`Invalid objective: ${JSON.stringify(objective)}`)
}

export function splitEconomicExposure(positions: PositionSetEquity[] | PositionSetOtherAsset[] | null) {
  if (!positions || positions.length === 0) {
    return { ...DEFAULT_MARKET_VALUES }
  }

  return positions.reduce(
    (acc, position) => {
      if (position.economicExposure < 0) {
        acc.shortMarketValue -= position.economicExposure
      } else {
        acc.longMarketValue += position.economicExposure
      }
      return acc
    }, { ...DEFAULT_MARKET_VALUES }) ?? { ...DEFAULT_MARKET_VALUES }
}

export function calculateCurrentMarketValues(portfolioHoldings: SelectedInlineInstrumentDate) {
  if (!portfolioHoldings) {
    return { ...DEFAULT_MARKET_VALUES }
  }

  const allPositions = [
    portfolioHoldings.equities,
    portfolioHoldings.currencies,
    portfolioHoldings.swaps,
    portfolioHoldings.fixedIncome,
    portfolioHoldings.commodities,
    portfolioHoldings.otherAssets,
  ]

  return allPositions.reduce(
    (totals, positions) => {
      const { longMarketValue, shortMarketValue } = splitEconomicExposure(positions)
      return {
        longMarketValue: totals.longMarketValue + longMarketValue,
        shortMarketValue: totals.shortMarketValue + shortMarketValue,
      }
    },
    { ...DEFAULT_MARKET_VALUES },
  )
}

export function calculateApiMarketValues(
  netLong: number | undefined,
  equityChange: number | undefined,
  portfolioMarketValues: MarketValues,
) {
  // Don't calculate market values if netLong and equityChange are undefined/not provided
  // These values are used to calculate long and short market values, so if they are not provided,
  // we don't need to calculate anything
  if (netLong === undefined && equityChange === undefined) {
    return {}
  }
  const { longMarketValue: portfolioLmv, shortMarketValue: portfolioSmv } = portfolioMarketValues
  const totalGmv = portfolioLmv + portfolioSmv

  const equityChangeFactor = equityChange !== undefined ? equityChange / 100 : 0

  let targetLongMarketValue = portfolioLmv
  let targetShortMarketValue = portfolioSmv
  if (netLong !== undefined) {
    targetLongMarketValue = totalGmv * netLong / 100
    targetShortMarketValue = totalGmv * (1 - netLong / 100)
  }

  const calculatedLongMarketValue = targetLongMarketValue * (1 + equityChangeFactor)
  const calculatedShortMarketValue = targetShortMarketValue * (1 + equityChangeFactor)
  return { longMarketValue: calculatedLongMarketValue, shortMarketValue: calculatedShortMarketValue }
}

export function calculatePortfolioNetLong(portfolioMarketValues: MarketValues): number {
  const totalMarketValue = portfolioMarketValues.longMarketValue + portfolioMarketValues.shortMarketValue
  if (totalMarketValue === 0) {
    return totalMarketValue
  }

  return portfolioMarketValues.longMarketValue / totalMarketValue
}

export function applyConstraintDefaults<TFactorRule = FactorRuleDisplay>(
  constraints: Partial<OptimizationConstraints<TFactorRule>>,
): Partial<OptimizationConstraints<TFactorRule>> {
  return {
    tradeSwaps: constraints.tradeSwaps ?? false,
    maxTurnover: constraints.maxTurnover ?? 1,
    maxTrade: constraints.maxTrade ?? {},

    ...pickBy({
      minConcentration: constraints.minConcentration,
      maxConcentration: constraints.maxConcentration,
      longMarketValue: constraints.longMarketValue,
      shortMarketValue: constraints.shortMarketValue,
      exposure: constraints.exposure ?? [],
      composition: constraints.composition ?? [],
    }, (value) => !isNil(value)),
  } as Partial<OptimizationConstraints<TFactorRule>>
}

export function addMarketValueConstraints<TFactorRule = FactorRuleDisplay>(
  constraints: Partial<OptimizationConstraints<TFactorRule>>,
  portfolioMarketValues: MarketValues,
): Partial<OptimizationConstraints<TFactorRule>> {
  const { netLong, equityChange, ...additionalConstraints } = constraints
  const netLongPercent = netLong !== undefined ? netLong * 100 : undefined
  const equityChangePercent = equityChange !== undefined ? equityChange * 100 : undefined

  const marketValues = calculateApiMarketValues(netLongPercent, equityChangePercent, portfolioMarketValues)
  return {
    ...additionalConstraints,
    ...marketValues,
  } as Partial<OptimizationConstraints<TFactorRule>>
}

export function parsePercent(value: string | null | undefined): number | undefined {
  const parsed = parseFloat(value as string)
  return isNaN(parsed) ? undefined : parsed / 100
}

export function getPortfolioType(portfolioMarketValues: MarketValues): PortfolioType {
  const { longMarketValue, shortMarketValue } = portfolioMarketValues
  if (longMarketValue > 0 && shortMarketValue > 0) {
    return LONG_SHORT_PORTFOLIO_TYPE
  } else if (longMarketValue > 0) {
    return LONG_PORTFOLIO_TYPE
  } else if (shortMarketValue > 0) {
    return SHORT_PORTFOLIO_TYPE
  }
  return LONG_SHORT_PORTFOLIO_TYPE
}

export function transformCompositionConstraints(composition: FactorRuleDisplay, netMarketValue: number | null): ApiFactorRule | null {
  const { min, max, minEconomicExposure, maxEconomicExposure, id, groupById, ...compositionConfig } = composition
  if (isNil(min) && isNil(max)) return null
  const type = toUpper(composition.type)

  // Calculate economic exposure values if missing but percentages are present
  let calculatedMinEconomicExposure = minEconomicExposure
  let calculatedMaxEconomicExposure = maxEconomicExposure

  if (netMarketValue !== null) {
    if (!isNil(min)) {
      calculatedMinEconomicExposure = (min / 100) * netMarketValue
    }
    if (!isNil(max)) {
      calculatedMaxEconomicExposure = (max / 100) * netMarketValue
    }
  }

  // Return null if no limit values can be calculated (incomplete constraint)
  if (calculatedMinEconomicExposure === undefined && calculatedMaxEconomicExposure === undefined) {
    return null
  }

  const config = {
    id,
    type,
    ...(calculatedMinEconomicExposure !== undefined && { minEconomicExposure: calculatedMinEconomicExposure }),
    ...(calculatedMaxEconomicExposure !== undefined && { maxEconomicExposure: calculatedMaxEconomicExposure }),
  }

  if (type === "CLASSIFICATION") {
    return {
      ...omit(compositionConfig, ["value", "info", "name", "category", "groupById"]),
      ...config,
      classificationId: groupById,
    }
  }

  return config
}

export function transformExposureConstraint(constraint: { id: string, min?: number, max?: number, contentSetId?: string }) {
  // If neither min nor max is provided, we do not want to create an optimize query constraint
  const { id, min, max, contentSetId } = constraint
  if (isNil(min) && isNil(max)) return null

  return {
    id,
    exposureWeighting: EXPOSURE_CONSTRAINT_WEIGHTINGS.PERCENT,
    ...(!isNil(min) && { min }),
    ...(!isNil(max) && { max }),
    ...(!isNil(contentSetId) && { contentSetId }),
  }
}

export function transformFormToConfiguration(
  endDate: IsoDate,
  setup: SetupSection,
  rules: Partial<RulesSection> = {},
  portfolioMarketValues: MarketValues,
  universe: UniverseSection,
): OptimizationConfiguration {
  const objectives: OptimizationObjective[] = [getObjectiveFromSetup(setup)]
  const options: Partial<OptimizationOptions> = {}
  const forecast = setup.forecast && setup.forecast !== "none" ? setup.forecast : null

  const minConcentration = rules.concentration != null
    ? parsePercent(rules.concentration.minConcentration) : undefined
  const maxConcentration = rules.concentration != null
    ? parsePercent(rules.concentration.maxConcentration) : undefined

  const maxTrade = rules.maxTrade != null ? pickBy({
    percentADV: parseFloat(rules.maxTrade.percentADV),
    percentEquity: parseFloat(rules.maxTrade.percentEquity),
    percentOriginalEconomicExposure: parseFloat(rules.maxTrade.percentOriginalEconomicExposure),
  }, (value) => !isNaN(value)) : undefined

  const portfolioType = getPortfolioType(portfolioMarketValues)
  const shouldIncludeNetLong = portfolioType === LONG_SHORT_PORTFOLIO_TYPE
  const netLong = shouldIncludeNetLong && rules.netLong != null
    ? parsePercent(rules.netLong.longMarketValue)
    : undefined

  const constraints: Partial<OptimizationConstraints> = pickBy({
    tradeSwaps: rules.tradeSwaps || undefined,
    maxTurnover: parsePercent(rules.maxTurnover),
    minConcentration,
    maxConcentration,
    maxTrade,
    equityChange: parsePercent(rules.equityChange),
    netLong,
    exposure: rules.exposure || [],
    composition: rules.composition || [],
  }, value => !isNil(value))

  const long = universe.long?.filter ? {
    securitySearch: {
      filter: [universe.long.filter],
      ...(universe.long.count && { take: universe.long.count }),
    },
  } : undefined

  const short = universe.short?.filter ? {
    securitySearch: {
      filter: [universe.short.filter],
      ...(universe.short.count && { take: universe.short.count }),
    },
  } : undefined

  const longValue = universe.long?.value
  const shortValue = universe.short?.value

  const securities: Partial<OptimizationSecuritiesInput> = {
    ...(!isNil(longValue) && !isNil(shortValue) && longValue === shortValue && { longOrShort: long }),
    ...(!isNil(longValue) && longValue !== shortValue && { long }),
    ...(!isNil(shortValue) && shortValue !== longValue && { short }),
  }

  if (rules.preservePositionDirection) {
    options.constraints = {
      allowDirectionChange: rules.preservePositionDirection,
    }
  }

  return {
    on: endDate,
    objectives,
    constraints,
    options,
    forecast,
    universe,
    securities,
  }
}

export function formatMaxTurnover(maxTurnover: number | undefined): string | null {
  if (maxTurnover === undefined || maxTurnover === 1) {
    return null
  }
  return percentageNoSymbolFormatter(maxTurnover)
}

export function formatConcentration(minConcentration?: number, maxConcentration?: number): RulesSection["concentration"] {
  if (minConcentration === undefined && maxConcentration === undefined) {
    return null
  }
  return {
    minConcentration: percentageNoSymbolFormatter(minConcentration),
    maxConcentration: percentageNoSymbolFormatter(maxConcentration),
  }
}

export function formatMaxTrade(maxTrade: { percentADV?: number, percentEquity?: number, percentOriginalEconomicExposure?: number } | undefined): RulesSection["maxTrade"] {
  if (maxTrade === undefined || isEmpty(maxTrade)) {
    return null
  }
  return {
    percentADV: String(maxTrade.percentADV ?? 25),
    percentEquity: String(maxTrade.percentEquity ?? 25),
    percentOriginalEconomicExposure: String(maxTrade.percentOriginalEconomicExposure ?? 25),
  }
}

export function formatNetLong(netLong: number | null): RulesSection["netLong"] {
  if (netLong === null) {
    return null
  }
  return {
    longMarketValue: String(netLong),
    shortMarketValue: parseFloat((100 - netLong).toFixed(2)),
  }
}

export function formatPreserveDirection(allowDirectionChange: boolean | undefined): boolean | null {
  if (allowDirectionChange === undefined || allowDirectionChange === false) {
    return null
  }
  return allowDirectionChange
}

export function transformConfigurationToForm(
  configuration: Partial<GraphQLOptimizationConfiguration>,
): FormSections {
  const objectives = configuration.objectives

  if (!objectives || objectives.length === 0) {
    throw new Error("No objectives found in configuration")
  }

  const objective = objectives[0]
  const setup = getSetupFromObjective(objective)
  const constraints = configuration.constraints || {}
  const options = configuration.options || {}
  const forecast = configuration.forecast || null
  if (forecast) {
    set(setup, "forecast", forecast)
  }

  let netLong: number | null = null
  if (constraints.netLong !== undefined) {
    netLong = parseFloat((constraints.netLong * 100).toFixed(2))
  }

  const hasConcentration = constraints.minConcentration !== undefined || constraints.maxConcentration !== undefined
  const rules: RulesSection = {
    tradeSwaps: constraints.tradeSwaps || null,
    maxTurnover: formatMaxTurnover(constraints.maxTurnover),
    concentration: hasConcentration ? formatConcentration(constraints.minConcentration, constraints.maxConcentration) : null,
    maxTrade: formatMaxTrade(constraints.maxTrade),
    equityChange: constraints.equityChange !== undefined
      ? percentageNoSymbolFormatter(constraints.equityChange)
      : null,
    netLong: formatNetLong(netLong),
    preservePositionDirection: formatPreserveDirection(options.constraints?.allowDirectionChange),
    exposure: constraints.exposure || [],
    composition: constraints.composition || [],
  }

  const universe = configuration.universe || { long: null, short: null }

  return {
    setup,
    rules,
    universe,
  }
}
